import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Spin, Alert } from "antd";
import { oktaAuth } from "config/okta";
import { useAuth } from "context/AuthContext";

const LoginCallback: React.FC = () => {
  const { setUnauthorizedState } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we have authorization code or if this is an error response
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get("code");
        const state = urlParams.get("state");
        const error = urlParams.get("error");
        const errorDescription = urlParams.get("error_description");
        
        // Check for error responses from Okta
        if (error) {
          console.error("Okta returned error:", error, errorDescription);
          if (error === "access_denied") {
            console.log("User denied access or not authorized");
            setUnauthorizedState?.();
            return;
          }
          throw new Error(`Okta error: ${error} - ${errorDescription}`);
        }
        
        // Verify we have required parameters
        if (!authCode || !state) {
          throw new Error("Missing authorization code or state parameter");
        }
        
        await oktaAuth.handleLoginRedirect();
        
        const authState = await oktaAuth.authStateManager.getAuthState();
        
        if (authState?.isAuthenticated) {
          // Check if user has required group for authorization
          const userGroups = Array.isArray(authState?.idToken?.claims?.groups) ? authState?.idToken?.claims?.groups as string[] : [];
          const authorizedGroup = process.env.REACT_APP_OKTA_AUTHORIZED_GROUP;
          
          // Check if user has required group for authorization
          if (authorizedGroup && !userGroups.includes(authorizedGroup)) {
            console.log("User authenticated but access denied - insufficient permissions");
            setUnauthorizedState?.();
            return;
          }
          
          console.log("User authenticated and authorized");
          setIsAuthenticated(true);
        } else {
          setError("Authentication failed. Please try again.");
        }
      } catch (err: any) {
        console.error("Login callback error:", err.message || err);

        // Check if it's an authorization error (user authenticated but not authorized)
        if (err?.error === "access_denied" ||
            err?.errorCode === "E0000006" ||
            err?.status === 401 ||
            err?.status === 403 ||
            (err?.message && err.message.includes("access_denied"))) {
          console.log("User authenticated but access denied - insufficient permissions");
          setUnauthorizedState?.();
          return;
        }

        setError("An error occurred during login. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void handleCallback();
  }, [setUnauthorizedState]);

  if (loading) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh",
        flexDirection: "column" 
      }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Completing sign in...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh" 
      }}>
        <Alert
          message="Authentication Error"
          description={error}
          type="error"
          showIcon
          action={
            <a href="/">Return to Login</a>
          }
        />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <Navigate to="/" replace />;
};

export default LoginCallback;